import { FormEvent, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { atoms, Button, colors, Link, TextInput } from '@zapier/marketing-ui';
import { mediaQueries, urls } from '@zapier/identity-utils';
import {
  SignupData,
  SignupErrors,
  SignupFieldFlags,
  SignupStatus,
} from '../../hooks';
import PasswordRequirements from './PasswordRequirements';
import { Controller, useFormContext } from 'react-hook-form';
import passwordValidation from './passwordValidation';
import { Legal } from './Legal';
import { QueryParams } from '../../types';
import { addParamsToUrl } from '@zapier/url-utils';

type Props = {
  formData: SignupData;
  formErrors: SignupErrors;
  handleFocus: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  invitation?: string;
  queryParams?: QueryParams;
  shouldDisableEmailField: boolean;
  shouldFocusEmailField: boolean;
  shouldShowEmailField: boolean;
  shouldShowNameFields: boolean;
  shouldShowPasswordField: boolean;
  status: SignupStatus;
  submittedFields: SignupFieldFlags;
};

const styles = {
  row: css`
    margin-bottom: 20px;
  `,

  name: css`
    display: grid;
    grid-gap: 20px 16px;
    ${mediaQueries.desktop} {
      grid-template-columns: 1fr 1fr;
    }
  `,

  password: css`
    position: relative;
    margin-bottom: 12px;
  `,

  passwordVisibility: css`
    position: absolute;
    right: 8px;
    top: 35px;
    background: ${colors.neutral100};
    border: none;
    color: ${colors.neutral700};
    cursor: pointer;
    font-size: 14px;
    padding: 4px 8px;
    transition: color 150ms;

    &:hover {
      color: ${colors.neutral900};
    }
  `,

  // Hide the fields in a very "creative" way so that
  // password managers still consider them visible and
  // fill them in. `display: none` doesn't work for all
  // password managers.
  hidden: css`
    margin-bottom: 0;
    position: relative;
    overflow: hidden;

    * {
      position: absolute !important;
      left: 100vw !important;
    }
  `,
};

export function UserInformation({
  formData,
  formErrors,
  handleFocus,
  handleSubmit,
  invitation,
  queryParams,
  shouldDisableEmailField,
  shouldFocusEmailField,
  shouldShowEmailField,
  shouldShowNameFields,
  shouldShowPasswordField,
  submittedFields,
  status,
}: Props) {
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    setFocus,
    formState: { errors },
  } = useFormContext<SignupData>();

  useEffect(() => {
    if (shouldShowEmailField && shouldFocusEmailField) {
      setFocus('email');
    }

    if (shouldShowPasswordField) {
      setFocus('password');
    }
  }, [
    setFocus,
    shouldShowEmailField,
    shouldFocusEmailField,
    shouldShowPasswordField,
  ]);

  return (
    <form onSubmit={handleSubmit}>
      <div
        css={[styles.row, !shouldShowEmailField && styles.hidden]}
        aria-hidden={!shouldShowEmailField}
        // `inert` removes the offscreen-hidden subtree from the focus order
        // entirely, so the `aria-hidden` subtree contains no focusable
        // descendants (satisfies axe's `aria-hidden-focus` rule). Unlike
        // `disabled`, it does not cause react-hook-form to drop the field's
        // value from submission. `|| undefined` ensures the attribute is
        // omitted (not rendered as `inert="false"`) when the field is shown.
        inert={!shouldShowEmailField || undefined}
        data-testid="emailField-wrapper"
      >
        <Controller
          name="email"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextInput
              {...field}
              id="signup-email"
              label="Work email *"
              type="email"
              inputMode="email"
              autoComplete="email"
              disabled={
                shouldDisableEmailField ||
                status === 'submitting' ||
                status === 'success'
              }
              error={
                status === 'error' && submittedFields?.email
                  ? formErrors.email ?? errors.email?.message?.toString()
                  : ''
              }
              tabIndex={shouldShowEmailField ? undefined : -1}
              onFocus={handleFocus}
            />
          )}
        />
      </div>
      <div
        css={[styles.row, styles.name, !shouldShowNameFields && styles.hidden]}
        aria-hidden={!shouldShowNameFields}
        inert={!shouldShowNameFields || undefined}
        data-testid="nameFields-wrapper"
      >
        <Controller
          name="firstName"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextInput
              {...field}
              id="signup-first-name"
              label="First name *"
              type="text"
              autoComplete="given-name"
              disabled={status === 'submitting' || status === 'success'}
              error={
                status === 'error' && submittedFields?.firstName
                  ? formErrors.firstName ??
                    errors.firstName?.message?.toString()
                  : ''
              }
              tabIndex={shouldShowNameFields ? undefined : -1}
              onFocus={handleFocus}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextInput
              {...field}
              id="signup-last-name"
              label="Last name *"
              type="text"
              autoComplete="family-name"
              disabled={status === 'submitting' || status === 'success'}
              error={
                status === 'error' && submittedFields?.lastName
                  ? formErrors.lastName ?? errors.lastName?.message?.toString()
                  : ''
              }
              tabIndex={shouldShowNameFields ? undefined : -1}
              onFocus={handleFocus}
            />
          )}
        />
      </div>
      <div
        css={[styles.row, !shouldShowPasswordField && styles.hidden]}
        aria-hidden={!shouldShowPasswordField}
        inert={!shouldShowPasswordField || undefined}
        data-testid="passwordField-wrapper"
      >
        <Controller
          name="password"
          control={control}
          rules={{
            validate: password =>
              passwordValidation(password, shouldShowPasswordField),
            required: shouldShowPasswordField,
          }}
          render={({ field }) => (
            <div css={styles.password}>
              <TextInput
                {...field}
                id="signup-password"
                label="Password *"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                disabled={status === 'submitting' || status === 'success'}
                error={
                  status === 'error' && submittedFields?.password
                    ? formErrors.password ??
                      errors.password?.message?.toString()
                    : ''
                }
                tabIndex={shouldShowPasswordField ? undefined : -1}
                onFocus={handleFocus}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                css={styles.passwordVisibility}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          )}
        />
        <div data-testid="password-requirements">
          <PasswordRequirements
            password={formData.password}
            passwordErrors={errors.password?.message}
          />
        </div>
      </div>
      <div css={styles.row}>
        <Legal />
      </div>
      <Button
        css={styles.row}
        fullWidth={true}
        variant="brand"
        disabled={status === 'submitting' || status === 'success'}
        type="submit"
        id="signup-submit"
      >
        Get started for free
      </Button>
      <p
        className={`${atoms.bodySM} ${atoms.textNeutral800} ${atoms.textCenter}`}
      >
        Already have an account?{' '}
        <Link
          href={addParamsToUrl(
            urls.LOGIN_URL,
            { invitation, ...queryParams },
            true
          )}
        >
          Log In
        </Link>
      </p>
    </form>
  );
}
